// File: frontend/src/utils/entryContentUtils.ts
// ─── Entry content lowercase normalization (sözlük convention) ────────────────
//
// Mirrors backend EntryContentNormalizer.java — keep the two implementations in
// sync. Entry content is always stored lowercase, using
// Turkish locale rules so dotted/dotless I is handled correctly
// ("İ" → "i", "I" → "ı").
//
// The following segments are NEVER lowercased, because changing their case
// would break navigation links or media URLs:
//   * The "||tail" (id / slug) part of reference tokens:
//     (mac: label||matchId), (ref: label||matchId), (kisi: name||slug),
//     (kulup: name||id), (takim: label||id), (baslik: title||id)
//     and their "(bkz: ...)" variants with the "bkz-" keyword prefix
//     (bkz-mac, bkz-kisi, bkz-kulup, bkz-takim, bkz-baslik)
//   * Legacy refs without "||": (ref: slug--id) / (bkz: slug) — preserved whole
//   * Inline image tokens: (gorsel: https://...) — media URLs are case-sensitive
//   * The URL part of (link: url|display) — only the display text is lowercased
//   * Bare URLs in plain text (https?://...)
//
// The transformation is idempotent.

/** Turkish-aware lowercase for a plain string (İ→i, I→ı). */
export function toLowerTr(value: string): string {
  return value.toLocaleLowerCase("tr");
}

/**
 * Strips a trailing disambiguation suffix in parentheses from a ref label
 * before it is inserted into entry content, e.g. "fer (dizi)" -> "fer".
 *
 * Titles/names across all 5 content domains (Gündem, Maç, Kulüp, Kişi, Takım)
 * sometimes carry a "(dizi)" / "(film)" / "(futbolcu)" style suffix purely to
 * disambiguate same-named records in search results and admin panels. That
 * suffix is useful for picking the right result in the ref search popup, but
 * it should not leak into the inline label the reader sees in an entry — the
 * "||tail" (slug/id) still points at the exact disambiguated record, so no
 * information is lost.
 *
 * No-op when there is no trailing "(...)" — plain names/titles pass through
 * unchanged. Only a single trailing parenthetical is stripped, so a genuine
 * parenthetical in the middle of a title is left alone.
 */
export function stripDisambiguationSuffix(label: string): string {
  return label.replace(/\s*\([^()]*\)\s*$/, "").trim();
}

// Placeholder sentinel from the Unicode Private Use Area — real user content
// will not contain this character, and it survives toLocaleLowerCase unchanged.
const PH = "\uE000";

// ── Protected-segment patterns (see header comment) ──────────────────────────
// Whole (gorsel: ...) token — protected as a whole. Legacy URLs are
// case-sensitive on the media server, and opaque image ids must survive
// lowercasing byte for byte because they address a file on disk.
const GORSEL_TOKEN = /\(gorsel:\s*(?:https?:\/\/[^)\s]+|[a-f0-9]{32}\.(?:jpg|jpeg|png|webp))\s*\)/g;
// Reference tokens — only the tail after "||" is protected
const REF_TOKEN_TAIL = /(\((?:bkz-)?(?:mac|ref|kisi|kulup|takim|baslik):\s*.*?\|\|)([^)]+)(\))/g;
// Legacy (ref/bkz: slug) tokens — preserved verbatim as a whole
const LEGACY_REF_TOKEN = /\((?:ref|bkz):\s*[^\s)]+\)/g;
// (link: url|display) — only the URL part is protected
const LINK_URL = /(\(link:\s*)([^|)]+)(\|)/g;
// Bare URLs in plain text
const BARE_URL = /https?:\/\/[^\s)]+/g;

/**
 * Lowercases entry content with Turkish locale rules while preserving URLs
 * and reference-token ids/slugs.
 */
export function lowercaseEntryContent(content: string): string {
  if (!content) return content;

  const store: string[] = [];
  const protect = (segment: string): string => {
    store.push(segment);
    return `${PH}${store.length - 1}${PH}`;
  };

  let working = content;

  // Step 1 — protect whole (gorsel: ...) tokens
  working = working.replace(GORSEL_TOKEN, (m) => protect(m));

  // Step 2 — protect only the "||tail" ids/slugs of reference tokens
  working = working.replace(REF_TOKEN_TAIL, (_m, head, tail, close) =>
    `${head}${protect(tail)}${close}`
  );

  // Step 3 — protect whole legacy (ref/bkz: slug) tokens
  working = working.replace(LEGACY_REF_TOKEN, (m) => protect(m));

  // Step 4 — protect only the URL part of (link: url|display) tokens
  working = working.replace(LINK_URL, (_m, head, url, pipe) =>
    `${head}${protect(url)}${pipe}`
  );

  // Step 5 — protect remaining bare URLs in plain text
  working = working.replace(BARE_URL, (m) => protect(m));

  // Step 6 — Turkish-aware lowercase of everything that is left
  working = working.toLocaleLowerCase("tr");

  // Step 7 — restore the protected segments in place of the placeholders
  return working.replace(
    new RegExp(`${PH}(\\d+)${PH}`, "g"),
    (m, idx) => store[Number(idx)] ?? m
  );
}